import { VATSummary, ReceiptFormData, CustomerInformation } from 'common/receipt.types';
import { formatDate, getSystemRegistrationTime } from 'common/utils/formatDate';
import { EcoTaxAdditionalProperties } from '../ecoTaxService/ecoTaxService.types';

export interface Workstation {
  countryCode: string;
  businessUnitId: string;
  workstationId: string;
  tenantId: string;
  operatorId: string;
  jwt: string;
}

type  ItemQuantity = {
  value: number;
  unitOfMeasure: string;
};

export interface RegisterTransactionResponse {
  localizedText: string;
  text: string;
  code: number;
}

export type TransformedLineItem = {
  lineItemNumber: number;
  lineDepositAndFee?: {
    depositAndFee: {
      depositAndFeeId: string;
      depositAndFeeName: string;
      depositAndFeeType: string;
      depositAndFeeCode?: string;
    },
    quantity: ItemQuantity;
    unitPrice: number;
    taxableGroupId: string;
    totalSaleAmount: number;
    additionalProperties?: EcoTaxAdditionalProperties[],
  };
  item: {
    itemId: string;
    name: string;
    itemDescription: string;
  };
  quantity: ItemQuantity;
  unitPrice: number;
  taxableGroupId: string;
  totalSaleAmount: number;
  additionalProperties?: EcoTaxAdditionalProperties[],
};

export type RegisterTransactionRequest = {
  businessUnitId: string;
  operatorId: string;
  workstationId: string;
  documentType: 'EXTENDED_RECEIPT';
  systemRegistrationDate: string;
} & {
  vatSummary: {
    taxTotal: number;
    netTotal: number;
    grossTotal: number;
    perRate: VATSummary[];
  };
} & {
  lineItems: TransformedLineItem[];
} & Pick<ReceiptFormData, 'referencedFiscalNumber' | 'customer' | 'invoiceDate'>;

export const sumAmounts = (amounts: VATSummary[], key: keyof VATSummary) =>
  amounts.reduce((accumulator, currentValue) => accumulator + Number(currentValue[key]), 0);

const getFilteredCustomerFields = (customer?: CustomerInformation) => {
  if (!customer) return undefined;
  
  const filteredCustomer = Object.fromEntries(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(customer).filter(([_, value]) => value)
  );
  
  return Object.keys(filteredCustomer).length > 0 ? filteredCustomer : undefined;
};

export const toTransactionCreateRequest = (
  formData: ReceiptFormData,
  {
    businessUnitId,
    workstationId,
    operatorId,
  }: Pick<Workstation, 'businessUnitId' | 'workstationId' | 'operatorId'>,
): RegisterTransactionRequest => {
  const filteredCustomer = getFilteredCustomerFields(formData?.customer);

  return ({
    businessUnitId,
    workstationId,
    operatorId,
    ...(filteredCustomer && { customer: filteredCustomer }),
    documentType: 'EXTENDED_RECEIPT',
    systemRegistrationDate: getSystemRegistrationTime(),
    invoiceDate: formatDate(formData.invoiceDate),
    referencedFiscalNumber: formData.referencedFiscalNumber,
    vatSummary: {
      netTotal: sumAmounts(formData.VATSummary, 'basePrice'),
      taxTotal: sumAmounts(formData.VATSummary, 'VATAmount'),
      grossTotal: Number(formData.grossTotal),
      // @ts-ignore
      perRate: formData.VATSummary.map((row) => ({
        basePrice: Number(row.basePrice),
        vatAmount: Number(row.VATAmount),
        taxableGroupId: row.taxableGroupId,
      })),
    },
    lineItems: formData.lineItems.map((line, index) => ({
      lineItemNumber: index + 1,
      ...(line.item.depositAndFeeRuleIds.length !== 0 && {
        lineDepositAndFees: line.item.ecoTaxItems.map((ecoTax) => ({
          depositAndFee: {
            depositAndFeeId: ecoTax.id,
            depositAndFeeName: ecoTax.name,
            depositAndFeeType: ecoTax.type,
            depositAndFeeCode: ecoTax.code,
          },
          quantity: {
            value: Number(ecoTax.quantity),
            unitOfMeasure: line.item.unitOfMeasure || line.item.intlUnitOfMeasure,
          },
          unitPrice: Number(ecoTax.unitPrice),
          totalSaleAmount: Number(ecoTax.totalPrice),
          taxableGroupId: ecoTax.taxableGroupId,
          additionalProperties: ecoTax.additionalProperties,
        })),
      }),
      item: {
        itemId: line.item.id,
        name: line.item.name,
        itemDescription: line.item.description,
      },
      quantity: {
        unitOfMeasure: line.item.unitOfMeasure,
        value: Number(line.item.quantity),
      },
      unitPrice: Number(line.item.unitPrice),
      totalSaleAmount: Number(line.item.totalPrice),
      taxableGroupId: line.item.taxableGroupId,
      additionalProperties: line.item.additionalProperties,
    })),
  });
};
